export const DEFAULT_PROCESSING_FEE_CHECKBOX_LABEL = 'Help cover processing fees';
export const DEFAULT_PROCESSING_FEE_CONTRIBUTION_LABEL = 'Processing fee contribution';

export type CoverProcessingFeesSettings = {
  enabled?: boolean | null;
  percentage?: number | null;
  amount?: number | null;
  label?: string | null;
  showAmount?: boolean | null;
  preselected?: boolean | null;
};

export type CoverProcessingFeesPage = {
  coverProcessingFees?: CoverProcessingFeesSettings[] | null;
};

// The page stores a list so it can offer several options later; checkout offers only the first.
export function getCoverProcessingFeesSetting(
  page: CoverProcessingFeesPage | null | undefined,
): CoverProcessingFeesSettings | null {
  return page?.coverProcessingFees?.[0] ?? null;
}

export type ProcessingFeeContribution = {
  amount: number;
  percentage: number;
  percentageAmount: number;
  fixedAmount: number;
};

export function hasProcessingFeeRate(settings?: CoverProcessingFeesSettings | null): boolean {
  return Boolean((settings?.percentage ?? 0) > 0 || (settings?.amount ?? 0) > 0);
}

export function isCoverProcessingFeesOffered(
  settings: CoverProcessingFeesSettings | null | undefined,
  total: number,
): boolean {
  return Boolean(settings?.enabled) && hasProcessingFeeRate(settings) && total > 0;
}

// `total` is everything else the customer pays, tax included, in minor units. Best effort by design:
// the seller's rate x total + fixed, never grossed up for Stripe's fee on it or our plan and platform fees.
export function getProcessingFeeContribution(
  settings: CoverProcessingFeesSettings | null | undefined,
  total: number,
): ProcessingFeeContribution | null {
  if (!isCoverProcessingFeesOffered(settings, total)) return null;
  const percentage = settings?.percentage ?? 0;
  const percentageAmount = Math.round((percentage / 100) * total);
  const fixedAmount = Math.round(settings?.amount ?? 0);
  return { amount: percentageAmount + fixedAmount, percentage, percentageAmount, fixedAmount };
}

export function getProcessingFeeCheckboxLabel(
  settings: CoverProcessingFeesSettings | null | undefined,
  contribution: ProcessingFeeContribution | null,
  formatAmount: (amount: number) => string,
): string {
  const label = settings?.label?.trim() || DEFAULT_PROCESSING_FEE_CHECKBOX_LABEL;
  const showAmount = settings?.showAmount ?? true;
  return showAmount && contribution ? `${label} (${formatAmount(contribution.amount)})` : label;
}

export type SubscriptionAmountSource = {
  amount?: number | null;
  processingFeeContribution?: { renewalAmount?: number | null } | null;
};

// `subscription.amount` carries each renewal's processing fee contribution, but the Stripe items,
// coupon maths and the Order build on the recurring price without it.
export function getSubscriptionAmountExcludingContribution(
  subscription: SubscriptionAmountSource,
): number {
  return (subscription.amount ?? 0) - (subscription.processingFeeContribution?.renewalAmount ?? 0);
}
